/**
 * alert.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import { ErrorObserver } from "rxjs/Observer";
// import { PresentDebugMessage } from "app/server/messages/index";
import { SettingService } from "../configuration/setting.service";
import { Announcement } from "./announcement";
import { BehaviorSubject } from "rxjs";
import { Router } from "@angular/router";

export type AlertType = "error" | "warning" | "notification" | "user"
  | "success" | "countdown";

declare const setTimeout :
	(cb : (...args : any[]) => void, time : number) => number;
	
const ANNOUNCEMENT_URL : string = "/zone/documents/announcements.json";

const RETRIEVAL_INTERVAL : number = 1000 * 60 * 60; // hourly

/**
 * An {@link Alert} is an encapsulated notification to the user.
 */
export interface Alert {
  message : string;
  name? : string;
  replacePrevious? : boolean;
  type? : AlertType;
  data? : any;
  timeout? : number | 'default';
  timer? : number;
  show? : boolean; // for controlling animation visibility
  suppressed? : boolean;
  actionLabel? : string;
  action? : (r : Router) => void;
}

/**
 * A map of the AlertTypes to callbacks for different kinds of console logging
 */
const CONSOLE_BEHAVIOR : Map<AlertType, (content : any) => void>
  = new Map([
    ["error", (content : any) => console.error(content)],
    ["warning", (content : any) => console.warn(content)],
    ["notification", (content : any) => console.info(content)],
    ["user", (content : any) => console.info(content)],
    ["success", (content : any) => console.log(content)],
    ["countdown", (content : any) => console.warn(content)]
] as Array<[AlertType, (content : any) => void]>);

/**
 * The {@link AlertService} is responsible for collecting and displaying various
 * kinds of notifications to the user on an application-wide level.
 */
@Injectable()
export class AlertService
{
  constructor (protected settings : SettingService, protected http : Http)
  {
		this.loadAnnouncements();
		// setInterval(() => this.loadAnnouncements(), RETRIEVAL_INTERVAL);
	}
	
	public announcementsLoaded : BehaviorSubject<boolean> =
		new BehaviorSubject(false);
	public userAnnouncements : Announcement[] = [];
	public guestAnnouncements : Announcement[] = [];

  public suppressedLevels : Set<AlertType> = new Set();

  private isAnnouncementLike (x : any) : x is Announcement
  {
    return (typeof x === "object" && "start" in x && "content" in x);
	}
	
	private loadAnnouncements () : void
	{
		this.http.get(ANNOUNCEMENT_URL).subscribe(
      (res : Response) => {
        const json : any = res.json();
        if (!Array.isArray(json))
        {
          console.error("Could not read announcements file. [Non-array root!]");
				}
				const readDate : number = Date.now();
				json.filter(this.isAnnouncementLike).filter(
					(a : Announcement) =>
						a.start < readDate && (a.expires == null || a.expires > readDate)
				).forEach((a : Announcement) => {
					if (a.unauthenticated === true)
					{
						this.guestAnnouncements.push(a);
					}
					else
					{
						this.userAnnouncements.push(a);
					}
				});
				
				if (this.guestAnnouncements.length > 0)
				{
					this.add({
						message: this.guestAnnouncements[0].content,
						type: AlertService.alertTypeFromAnnouncement(
							this.guestAnnouncements[0]),
						timeout: null
					});
				}
				this.announcementsLoaded.next(true);
			});
	}

	private static alertTypeFromAnnouncement (a : Announcement) : AlertType
	{
		switch (a.style)
		{
			case "success":
				return "success";
			case "warning":
				return "warning";
			case "danger":
				return "error";
			case "info":
			default:
				return "notification";
		}
	}

  /** An array of the currently active alerts */
  public alerts : Alert[] = [];
  
  /**
   * The *named* alerts, which are usually done in order to allow other services
   * to dismiss the alert when the condition passes.
   */
  public namedAlerts : Map<string, Alert> = new Map();

  /** Whether alerts should be logged to the console as well */
  public consoleDuplication : boolean = true;

  protected shouldSuppress (alert : Alert) : boolean
  {
    if (this.suppressedLevels.has(alert.type)) {
      return true;
    }
    return false;
  }

  /**
   * Add an {@link Alert} to the current set
   */
  public add (alert : Alert) : boolean
  {
    if (alert.name)
    {
      if (this.namedAlerts.has(alert.name))
      {
        if (alert.replacePrevious === true)
        {
          this.remove(this.namedAlerts.get(alert.name), true);
        }
        else
        {
          // Without replacement, we only allow one alert of each name
          return false;
        }
      }
      this.namedAlerts.set(alert.name, alert);
    }
    this.alerts.push(alert);
    if (this.shouldSuppress(alert)) {
      alert.suppressed = true;
    }
    if (alert.timeout)
    {
      if (alert.timeout === 'default') {
        alert.timeout = this.getDefaultTimeout();
      }
      alert.timer = setTimeout(() => this.remove(alert), alert.timeout);
    }
    this.fadeIn(alert);

    if (
      this.consoleDuplication
      && alert.type
      && CONSOLE_BEHAVIOR.has(alert.type)
    )
    {
      CONSOLE_BEHAVIOR.get(alert.type)(alert.message);
      if (alert.data)
      {
        CONSOLE_BEHAVIOR.get(alert.type)(alert.data);
      }
    }
    return true;
  }

  /**
   * Retrieve the current timeout length setting, used as a default.
   */
  public getDefaultTimeout () : number
  {
    return this.settings.get("alert timeout");
  }

  /**
   * Construct a default {@link Alert} by message/type and add it to the array.
   */
  protected addStandard (message : string, type : AlertType) : void
  {
    this.add({
      message,
      type,
      timeout: this.getDefaultTimeout()
    });
  }

  /**
   * Set a timeout to handle visibility transition on
   */
  private fadeIn (alert : Alert) : void
  {
    setTimeout(() => {
      const pos : number = this.alerts.indexOf(alert);
      if (pos === -1)
      {
        return false;
      }
      this.alerts[pos].show = true;
    }, 20);
  }

  /**
   * Cancel a close timeout and keep the alert on-screen until manually closed
   */
  public cancelTimeout (alert : Alert) : void
  {
    if (alert.timer)
    {
      clearTimeout(alert.timer);
      alert.timer = null;
    }
  }

  /**
   * Transition visibility off and set a timeout to completely remove the alert
   */
  public remove (alert : Alert, immediate : boolean = false) : void
  {
    alert.show = false;
    this.cancelTimeout(alert);
    if (alert.name)
    {
      this.namedAlerts.delete(alert.name);
    }

    const remove : () => void = () => {
      const pos : number = this.alerts.indexOf(alert);
      if (pos === -1)
      {
        return false;
      }
      this.alerts.splice(pos, 1);
    };

    if (immediate)
    {
      remove();
    }
    else
    {
      setTimeout(remove, 150);
    }
  }

  // ----- The rest of the file is helper functions that simplify calls: -----

  /**
   * Create a callback that triggers error alerts when a subscribable errors.
   */
  public errorSubscriber (message : string) : ErrorObserver<any>
  {
    return {error: (err : any) => this.error(message)};
  }
  /**
   * Add an alert of the "error" type with a given message.
   */
  public error (message : string) : void
  {
    this.add({message, type: "error"});
  }
  /**
   * Add an alert of the "error" type with a given message, using a specified
   * name, and replacing the previous alert of that name, if any.
   */
  public errorAndReplace (message : string, name : string) : void
  {
    this.add({message, type: "error", name, replacePrevious: true});
  }

  /**
   * Add an alert of the "warning" type with a given message.
   */
  public warn (message : string) : void
  {
    this.addStandard(message, "warning");
  }

  /**
   * Add an alert of the "warning" type with a given message, using a specified
   * name, and replacing the previous alert of that name, if any.
   */
  public warnAndReplace (message : string, name : string) : void
  {
    this.add({message, type: "warning", name, replacePrevious: true,
      timeout: this.getDefaultTimeout()});
	}
	
	/**
   * Add an alert of the given type with a given message, using a specified
   * name, and replacing the previous alert of that name, if any.
   */
	public addAndReplace (message : string, type : AlertType, name : string)
		: void
  {
    this.add({message, type, name, replacePrevious: true,
      timeout: this.getDefaultTimeout()});
	}
	
  /**
   * Add an alert of the "notification" type with a given message.
   */
  public notify (message : string) : void
  {
    this.addStandard(message, "notification");
  }
  /**
   * Add an alert of the "success" type with a given message.
   */
  public success (message : string) : void
  {
    this.addStandard(message, "success");
  }
  /**
   * Add an alert of the "user" type with a given message.
   */
  public userInfo (message : string) : void
  {
    this.addStandard(message, "user");
  }

  /**
   * Remove the alert for a given name.
   * @param name The provided name of the alert.
   */
  public removeNamed (name : string) : void
  {
    if (this.namedAlerts.has(name))
    {
      this.remove(this.namedAlerts.get(name));
    }
  }

  public mute () : void
  {
    this.alerts = [];
    this.suppressedLevels = new Set(
      ["error", "warning", "notification", "user", "success", "countdown"] as AlertType[]
    );
  }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/alert.service.ts